/**
 * Merged itinerary preferences sub-FAB (`transports_prefs`).
 *
 * Opens a single MiniModal with tabbed panels for transit, shared vehicles,
 * bike, and car settings (swipe on mobile).
 *
 * Tab selection, swipe sync, and haptics are owned by {@link MiniModal}.
 */

import React, { useCallback } from "react";
import ItineraryPrefsIcon from "../../../../../image/icons/floating/ItineraryPrefsIcon.svg";
import SharedVehicleTabIcon from "../../../../../image/icons/floating/SharedVehicleTabIcon.svg";
import BicycleIcon from "../../../../../image/icons/map/transportIcons/BicycleIcon.svg";
import BusIcon from "../../../../../image/icons/map/transportIcons/BusIcon.svg";
import CarIcon from "../../../../../image/icons/map/transportIcons/CarIcon.svg";
import {
    fabUiSlice,
    usePersistedSlice,
    type TransportsPrefsTabId,
} from "../../../../../store/persistence";
import MiniModalSection from "../../../../MiniModal/MiniModalSection";
import MiniModalTabs, { MiniModalTab } from "../../../../MiniModal/MiniModalTabs";
import {
    BURST_SUB_FAB_GREEN_FILTER,
    FAB_ICON_SIZE_PX,
} from "../../../floatingAction.configuration";
import SubFloatingAction from "../SubFloatingAction";
import BikePrefsModalContent from "./BikePrefsModalContent";
import CarPrefsModalContent from "./CarPrefsModalContent";
import RentalVehiclesPrefsModalContent from "./RentalVehiclesPrefsModalContent";
import TransitPrefsModalContent from "./TransitPrefsModalContent";

/** Props exposed by the itinerary prefs sub-FAB. */
export interface TransportsPrefsSubFloatingActionProps {

    /** When `true`, use the dark palette variants. */
    "isDarkUi": boolean;

    /** Notified whenever the itinerary settings MiniModal open state changes. */
    "onOpenStateChange": ( isOpen: boolean ) => void;
}

/**
 * Itinerary preferences sub-FAB renderer.
 *
 * @param props Component props. See `TransportsPrefsSubFloatingActionProps`.
 * @returns The configured `SubFloatingAction` with the itinerary settings MiniModal.
 */
const TransportsPrefsSubFloatingAction: React.FC<TransportsPrefsSubFloatingActionProps> = ( {
    isDarkUi,
    onOpenStateChange,
} ) => {
    const [ fabUi, setFabUi ] = usePersistedSlice( fabUiSlice );
    const activeTabId = fabUi.lastTransportsPrefsTab;

    /**
     * Persists the last opened itinerary settings tab for the next modal open.
     *
     * @param tabId Tab id to remember.
     */
    const handleTabChange = useCallback( ( tabId: string ): void => {
        const allowedTabIds: readonly TransportsPrefsTabId[] = [ "bike", "car", "transit", "rental" ];
        if ( !allowedTabIds.includes( tabId as TransportsPrefsTabId ) ) {
            return;
        }

        setFabUi( ( previous ) => ( {
            ...previous,
            "lastTransportsPrefsTab": tabId as TransportsPrefsTabId,
        } ) );
    }, [ setFabUi ] );

    return (
        <SubFloatingAction
            ariaLabel="Paramètres des itinéraires"
            iconAlt="Itinerary preferences"
            iconFilter={BURST_SUB_FAB_GREEN_FILTER}
            iconSizePx={FAB_ICON_SIZE_PX}
            iconSrc={ItineraryPrefsIcon}
            isDarkUi={isDarkUi}
            kind="modal"
            modalDisableContentPaddingTop
            modalTitle="Préférences des itinéraires"
            onOpenStateChange={onOpenStateChange}
        >
            <MiniModalSection
                isDarkUi={isDarkUi}
            >
                <MiniModalTabs
                    activeTabId={activeTabId}
                    ariaLabel="Préférences des itinéraires"
                    className="mt-1"
                    iconOnly
                    isDarkUi={isDarkUi}
                    onActiveTabChange={handleTabChange}
                >
                    <MiniModalTab
                        iconSrc={BusIcon}
                        id="transit"
                        label="Transit"
                    >
                        <TransitPrefsModalContent isDarkUi={isDarkUi} />
                    </MiniModalTab>
                    <MiniModalTab
                        iconClassName="h-[13px] w-5"
                        iconSrc={SharedVehicleTabIcon}
                        id="rental"
                        label="Partagé"
                    >
                        <RentalVehiclesPrefsModalContent isDarkUi={isDarkUi} />
                    </MiniModalTab>
                    <MiniModalTab
                        iconSrc={BicycleIcon}
                        id="bike"
                        label="Vélo"
                    >
                        <BikePrefsModalContent isDarkUi={isDarkUi} />
                    </MiniModalTab>
                    <MiniModalTab
                        iconSrc={CarIcon}
                        id="car"
                        label="Voiture"
                    >
                        <CarPrefsModalContent isDarkUi={isDarkUi} />
                    </MiniModalTab>
                </MiniModalTabs>
            </MiniModalSection>
        </SubFloatingAction>
    );
};

export default TransportsPrefsSubFloatingAction;
